import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Icon from "components/ShellV2/Icon";

/* rec-styled table for SERVER-side pagination/sorting (counterpart of the
   client-side DataTable). The parent owns page/limit/sort state and the query.
   columns: [{key, label, align, width, render(value, row), sortable}] */
export default function RemoteTable({
  columns,
  rows,
  totalCount,
  page,
  limit,
  onPageChange,
  sort,
  onSortChange,
  filters,
  loading,
  emptyText,
}) {
  const { t } = useTranslation(["common"]);

  const pages = Math.max(1, Math.ceil(totalCount / limit));
  const safePage = Math.min(page, pages - 1);

  const toggleSort = (c) => {
    if (!c.sortable) return;
    const direction = sort?.column === c.key && sort?.direction === "asc" ? "desc" : "asc";
    onSortChange({ column: c.key, direction });
  };

  // window of up to 5 page buttons centred on the current page
  const windowStart = Math.max(0, Math.min(safePage - 2, pages - 5));
  const pageButtons = Array.from({ length: Math.min(pages, 5) }, (_, i) => windowStart + i);

  return (
    <div style={loading ? { opacity: 0.65, pointerEvents: "none" } : undefined}>
      {filters && <div className="rec-toolbar">{filters}</div>}
      <div className="rec-tbl-wrap">
        <table className="rec-tbl rec-tbl-cards">
          <thead>
            <tr>
              {columns.map((c) => {
                const isSorted = sort?.column === c.key;
                return (
                  <th
                    key={c.key}
                    style={{
                      textAlign: c.align || "left",
                      width: c.width,
                      cursor: c.sortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={c.sortable ? () => toggleSort(c) : undefined}
                  >
                    <span
                      className="inline-flex items-center gap-1"
                      style={{ flexDirection: c.align === "right" ? "row-reverse" : "row" }}
                    >
                      {c.label}
                      {c.sortable && (
                        <Icon
                          name={
                            isSorted
                              ? (sort.direction === "asc" && "arrow_upward") || "arrow_downward"
                              : "unfold_more"
                          }
                          style={{ fontSize: 14, opacity: isSorted ? 0.9 : 0.3 }}
                        />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id ?? i}>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    data-label={typeof c.label === "string" && c.label ? c.label : undefined}
                    style={{ textAlign: c.align || "left" }}
                  >
                    {c.render ? c.render(row[c.key], row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: "center", padding: 40, color: "var(--ink-3)" }}
                >
                  {emptyText || t("table.no_data", { defaultValue: "Sem dados disponíveis." })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalCount > 0 && (
        <div className="rec-tbl-foot">
          <span className="text-[13px] text-ink-3">
            {t("table.showing", {
              defaultValue: "A mostrar {{shown}} de {{total}} registos",
              shown: rows.length,
              total: totalCount,
            })}
          </span>
          <div className="rec-pager">
            <button
              type="button"
              disabled={safePage === 0}
              onClick={() => onPageChange(0)}
              aria-label={t("table.first_page", { defaultValue: "Primeira página" })}
            >
              <Icon name="first_page" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              disabled={safePage === 0}
              onClick={() => onPageChange(Math.max(0, safePage - 1))}
              aria-label={t("table.prev_page", { defaultValue: "Anterior" })}
            >
              <Icon name="chevron_left" style={{ fontSize: 18 }} />
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                type="button"
                className={safePage === p ? "on" : ""}
                onClick={() => onPageChange(p)}
              >
                {p + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={safePage >= pages - 1}
              onClick={() => onPageChange(Math.min(pages - 1, safePage + 1))}
              aria-label={t("table.next_page", { defaultValue: "Seguinte" })}
            >
              <Icon name="chevron_right" style={{ fontSize: 18 }} />
            </button>
            <button
              type="button"
              disabled={safePage >= pages - 1}
              onClick={() => onPageChange(pages - 1)}
              aria-label={t("table.last_page", { defaultValue: "Última página" })}
            >
              <Icon name="last_page" style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

RemoteTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node,
      align: PropTypes.oneOf(["left", "center", "right"]),
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      render: PropTypes.func,
      sortable: PropTypes.bool,
    })
  ).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  totalCount: PropTypes.number,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  sort: PropTypes.shape({ column: PropTypes.string, direction: PropTypes.string }),
  onSortChange: PropTypes.func,
  filters: PropTypes.node,
  loading: PropTypes.bool,
  emptyText: PropTypes.node,
};

RemoteTable.defaultProps = {
  totalCount: 0,
  sort: undefined,
  onSortChange: () => {},
  filters: undefined,
  loading: false,
  emptyText: undefined,
};
